import { AuthStorage } from "utils/auth-storage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Table,
  Input,
  InputNumber,
  Select,
  Popconfirm,
  Tooltip,
  Alert,
  Form,
} from "antd";
import { WarehouseServices } from "apis/Services/WarehouseService";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import LoadingComponent from "components/loading-component";
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import MaterialFormModal from "pages/warehouse-inventory/materialFormModal";

const LineItemsModal = ({
  record,
  isPostLineItemVisible,
  setIsPostLineItemVisible,
  setShouldReload = () => {},
  isProcLog = false,
  projectID,
  isTransferMode = false,
  materialsData = {},
  readOnlyMode = false,
  accountingMethod,
  updateMaterials,
  accessLevel,
}) => {
  const { company_id } = useParams();
  const [lineItems, setLineItems] = useState([]);
  const [originalLineItems, setOriginalLineItems] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [hasValidationMaterialsErrors, setHasValidationMaterialsErrors] =
    useState(false);
  const [unitsOptions, setUnitsOptions] = useState();
  const [wbsOptions, setWbsOptions] = useState();
  const [nextTempId, setNextTempId] = useState(1);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState([]);
  const [locationsOptions, setLocationsOptions] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const [form] = Form.useForm();

  const editedRows = React.useRef(new Set());
  const newRows = React.useRef(new Set());

  const allMaterials = useMemo(
    () =>
      Object.entries(materialsData).map(([id, item]) => ({
        materialID: id,
        ...item,
      })),
    [materialsData],
  );

  if (accessLevel < 2) {
    readOnlyMode = true;
  }

  const getMaterialById = useCallback(
    (itemID) => {
      return allMaterials.find(
        (item) => item.materialID === itemID?.toString(),
      );
    },
    [allMaterials],
  );

  const getData = useCallback(() => {
    setLoading(true);
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");
    let ReqObj = {
      company: company_id,
      projectID: projectID,
      purchaseOrderID: record?.purchaseOrderID,
      code,
      loginEmail: email,
    };
    WarehouseServices.getPurchaseOrderLineItemsFromProcore(ReqObj)
      .then((res) => {
        console.log(res, "RESPONSEEE");
        const itemsWithKeys = res.map((item, index) => ({
          ...item,
          key: item.lineItemID || index,
        }));

        setLineItems(itemsWithKeys);
        setOriginalLineItems(JSON.parse(JSON.stringify(itemsWithKeys)));
        setSelectedRowKeys(
          itemsWithKeys
            .filter((item) => item.transferStatus !== "Sent")
            .map((item) => item.key),
        );
      })
      .catch((err) => {
        console.log(err);
        setLineItems([]);
        setOriginalLineItems([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [company_id, projectID, record?.purchaseOrderID]);

  const getUnits = useCallback(() => {
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");
    let ReqObj = {
      company: company_id,
      project: projectID,
      code,
      loginEmail: email,
    };
    WarehouseServices.getInventorySettings(ReqObj)
      .then((res) => {
        setCategoryOptions(res.settingsDict.categories);
        setSubcategoryOptions(res.settingsDict.subcategories);
        setLocationsOptions(res.settingsDict.locations);
        setUnitsOptions(res.settingsDict.units);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [company_id, projectID]);

  const getWorkBreakDownStructureCode = useCallback(() => {
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");
    let ReqObj = {
      company: company_id,
      projectID: projectID,
      code,
      loginEmail: email,
    };
    WarehouseServices.getWorkBreakDownStructureCode(ReqObj)
      .then((res) => {
        console.log("WBS CODE IDs:", res);
        setWbsOptions(res);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [company_id, projectID]);

  const validateLineItems = useCallback((items) => {
    return items.some(
      (item) =>
        !item.description?.trim() ||
        !item.unit?.trim() ||
        !item.wbsCodeID?.toString().trim() ||
        item.quantity === null ||
        item.quantity === undefined ||
        item.quantity === "" ||
        item.unitCost === null ||
        item.unitCost === undefined ||
        item.unitCost === "",
    );
  }, []);

  const validateTransferItems = useCallback(
    (items) => {
      return items.some((item) => {
        if (!item.itemID?.toString().trim() || item.itemID === -1) {
          return true;
        }
        const material = getMaterialById(item.itemID);
        if (material && material.unit !== item.unit) {
          return true;
        }

        return false;
      });
    },
    [getMaterialById],
  );

  useEffect(() => {
    if (record?.purchaseOrderID && isPostLineItemVisible) {
      getData();
      getWorkBreakDownStructureCode();
    }
  }, [
    getData,
    getWorkBreakDownStructureCode,
    isPostLineItemVisible,
    record?.purchaseOrderID,
  ]);

  useEffect(() => {
    if (isPostLineItemVisible && !unitsOptions) {
      getUnits();
    }
  }, [getUnits, isPostLineItemVisible, unitsOptions]);

  useEffect(() => {
    const changes =
      JSON.stringify(lineItems) !== JSON.stringify(originalLineItems);
    setHasChanges(changes);

    const hasErrors = validateLineItems(lineItems);
    setHasValidationErrors(hasErrors);

    let itemsToValidateForMaterials;

    if (selectedRowKeys.length > 0) {
      itemsToValidateForMaterials = lineItems.filter(
        (item) =>
          selectedRowKeys.includes(item.key) && item.transferStatus !== "Sent",
      );
    } else {
      itemsToValidateForMaterials = lineItems.filter(
        (item) => item.transferStatus !== "Sent",
      );
    }

    const hasMaterialsErrors = validateTransferItems(
      itemsToValidateForMaterials,
    );
    setHasValidationMaterialsErrors(hasMaterialsErrors);
  }, [
    lineItems,
    originalLineItems,
    selectedRowKeys,
    validateLineItems,
    validateTransferItems,
  ]);

  const handleAddLineItem = () => {
    const tempId = `temp_${nextTempId}`;
    const newLineItem = {
      key: tempId,
      lineItemID: null,
      description: "",
      quantity: 0,
      unit: "",
      unitCost: 0,
      amount: 0,
      wbsCodeID: "",
      itemID: -1,
      transferStatus: "",
    };
    newRows.current.add(tempId);
    editedRows.current.add(tempId);
    setLineItems([...lineItems, newLineItem]);
    setNextTempId(nextTempId + 1);
  };

  const handleDeleteLineItem = (key) => {
    const updatedLineItems = lineItems.filter((item) => item.key !== key);
    setLineItems(updatedLineItems);

    editedRows.current.delete(key);
    newRows.current.delete(key);
  };

  const handleCellEdit = (key, dataIndex, value) => {
    editedRows.current.add(key);

    const newData = [...lineItems];
    const index = newData.findIndex((item) => item.key === key);
    if (index > -1) {
      newData[index] = {
        ...newData[index],
        [dataIndex]: value,
      };

      if (dataIndex === "quantity" || dataIndex === "unitCost") {
        const item = newData[index];
        const quantity = parseFloat(item.quantity) || 0;
        const unitCost = parseFloat(item.unitCost) || 0;
        newData[index].amount = (quantity * unitCost).toFixed(2);
      }

      setLineItems(newData);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let code = AuthStorage.getItem("token");
      let email = AuthStorage.getItem("email");

      const editedLineItems = lineItems.filter((item) =>
        editedRows.current.has(item.key),
      );
      const newLineItems = editedLineItems.filter((item) => !item.lineItemID);
      const updatedLineItems = editedLineItems.filter(
        (item) => item.lineItemID,
      );

      if (newLineItems.length > 0) {
        const newItemsData = newLineItems.map((item) => ({
          purchaseOrderID: record?.purchaseOrderID,
          amount: parseFloat(item.amount),
          description: item.description,
          quantity: parseFloat(item.quantity),
          unitCost: parseFloat(item.unitCost),
          unit: item.unit,
          wbsCodeID: item.wbsCodeID,
          itemID: item.itemID || -1,
          accountingMethod: accountingMethod,
        }));
        const newItemsPayload = {
          company: company_id,
          projectID: projectID,
          code,
          loginEmail: email,
          lineItems: newItemsData,
        };
        console.log(newItemsPayload, "new items payload");
        await WarehouseServices.postPurchaseOrderLineItemToProcore(
          newItemsPayload,
        );
      }

      if (updatedLineItems.length > 0) {
        const updatedItemsData = updatedLineItems.map((item) => ({
          lineItemID: item.lineItemID,
          description: item.description,
          quantity: parseFloat(item.quantity),
          unit: item.unit,
          unitCost: parseFloat(item.unitCost),
          amount: parseFloat(item.amount),
          wbsCodeID: item.wbsCodeID,
          itemID: item.itemID || -1,
          accountingMethod: accountingMethod,
        }));
        const updatedItemsPayload = {
          company: company_id,
          projectID: projectID,
          purchaseOrderID: record?.purchaseOrderID,
          code,
          loginEmail: email,
          lineItems: updatedItemsData,
        };
        console.log(updatedItemsPayload, "updated items payload");
        await WarehouseServices.updatePurchaseOrderLineItemInProcore(
          updatedItemsPayload,
        );
      }
      toast.success("Line items updated successfully!");
      getData();
      newRows.current.clear();
      editedRows.current.clear();
      setHasChanges(false);
    } catch (error) {
      console.error("Error saving line items:", error);

      const status = error?.status;

      if (status === 401) {
        toast.error("Failed to save in Koncurent.");
      } else if (status === 422) {
        toast.success("These changes are only reflected in Koncurent.");
        setHasChanges(false);
      } else {
        toast.error("Failed to save line items, please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTransfer = async (forceFullTransfer = false) => {
    setTransferring(true);
    try {
      let code = AuthStorage.getItem("token");
      let email = AuthStorage.getItem("email");

      console.log(editedRows.current, "EDITED ROWSSS");
      console.log(newRows.current, "NEW ROWSSS");

      let itemsToTransfer = [];

      if (selectedRowKeys.length > 0) {
        itemsToTransfer = lineItems.filter(
          (item) =>
            selectedRowKeys.includes(item.key) &&
            item.lineItemID &&
            item.transferStatus !== "Sent",
        );
      } else {
        itemsToTransfer = lineItems.filter(
          (item) => item.lineItemID && item.transferStatus !== "Sent",
        );
      }

      if (itemsToTransfer.length === 0) {
        toast.error("No unsent line items selected for transfer.");
        setTransferring(false);
        return;
      }

      const allItemsData = itemsToTransfer.map((item) => ({
        itemID: item?.itemID,
        purchaseOrderID: record?.purchaseOrderID,
        projectID: record?.projectID,
        lineItemID: item.lineItemID,
        PO_description: item.description,
        newQuantity: parseFloat(item.quantity),
        unit: item.unit,
        unitPrice: parseFloat(item.unitCost),
        PO_amount: parseFloat(item.amount),
        PO_wbsCodeID: item.wbsCodeID,
        accountingMethod: accountingMethod,
      }));

      const isFullTransfer = forceFullTransfer === true;

      // The primary "Transfer to Inventory" action should remain a partial
      // transfer unless the user explicitly chooses "Mark Completed".
      const transferDescription = isFullTransfer ? "full" : "partial";

      const ReqObj = {
        company: company_id,
        code,
        loginEmail: email,
        lineItemsTransferred: [...allItemsData],
        transferDescription: transferDescription,
      };

      console.log(ReqObj, "items payload for transfer");

      await WarehouseServices.sendKoncurentPurchaseOrdersToInventory(ReqObj);

      toast.success("Purchase order transferred successfully!");
      getData();
      newRows.current.clear();
      editedRows.current.clear();
      setHasChanges(false);
      setShouldReload(true);
    } catch (error) {
      console.error("Error saving line items:", error);
      toast.error("Failed to transfer purchase order, please try again.");
    } finally {
      setTransferring(false);
    }
  };

  const handleMarkPurchaseOrderCompleted = () => {
    const targetProjectID = projectID || record?.projectID;

    if (!targetProjectID || !record?.purchaseOrderID) {
      toast.error("Missing purchase order information.");
      return;
    }

    Modal.confirm({
      title: "Mark Purchase Order as Completed?",
      content:
        "This can't be undone. Once this purchase order is marked as completed, it can't be unmarked.",
      okText: "Mark as Completed",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        setCompleting(true);

        try {
          const code = AuthStorage.getItem("token");
          const email = AuthStorage.getItem("email");
          const ReqObj = {
            company: company_id,
            code,
            loginEmail: email,
            completedPurchaseOrders: [
              {
                projectID: String(targetProjectID),
                purchaseOrderID: String(record?.purchaseOrderID),
              },
            ],
          };

          await WarehouseServices.markPurchaseOrderCompleted(ReqObj);
          toast.success("Purchase order marked as completed successfully!");
          setShouldReload(true);
          onCancel();
        } catch (error) {
          console.error("Error marking purchase order as completed:", error);
          toast.error(
            "Failed to mark the purchase order as completed, please try again.",
          );
        } finally {
          setCompleting(false);
        }
      },
    });
  };

  const handleCancel = () => {
    if (hasChanges) {
      Modal.confirm({
        title: "Unsaved Changes",
        content: "You have unsaved changes. Are you sure you want to close?",
        onOk: () => {
          onCancel();
        },
      });
    } else {
      onCancel();
    }
  };

  const onCancel = () => {
    setIsPostLineItemVisible(false);
    setLineItems([]);
    setOriginalLineItems([]);
    setSelectedRowKeys([]);
    setHasChanges(false);
    setHasValidationErrors(false);
    setHasValidationMaterialsErrors(false);
    editedRows.current.clear();
    newRows.current.clear();
    setNextTempId(1);
  };

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    children,
    ...restProps
  }) => {
    const [value, setValue] = useState(record?.[dataIndex]);
    const [isEditing, setIsEditing] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    const isSent = record?.transferStatus === "Sent";

    const hasUnitMismatch = () => {
      if (!isTransferMode) return false;

      const material = getMaterialById(record?.itemID);
      const hasItemID =
        record?.itemID &&
        record?.itemID !== -1 &&
        record?.itemID?.toString().trim();
      const hasUnit = record?.unit && record?.unit?.trim();

      if (hasItemID && hasUnit && material) {
        return material.unit !== record.unit;
      }
      return false;
    };

    const hasError = () => {
      if (isSent) return false;

      if (
        (dataIndex === "itemID" || dataIndex === "unit") &&
        hasUnitMismatch()
      ) {
        return true;
      }

      if (dataIndex === "description") return !record?.description?.trim();
      if (dataIndex === "unit") return !record?.unit?.trim();
      if (dataIndex === "wbsCodeID")
        return !record?.wbsCodeID?.toString().trim();
      if (dataIndex === "quantity")
        return (
          record?.quantity === null ||
          record?.quantity === undefined ||
          record?.quantity === ""
        );
      if (dataIndex === "unitCost")
        return (
          record?.unitCost === null ||
          record?.unitCost === undefined ||
          record?.unitCost === ""
        );

      return false;
    };

    useEffect(() => {
      setValue(record?.[dataIndex]);
    }, [record, dataIndex]);

    const handleSave = () => {
      editedRows.current.add(record.key);
      handleCellEdit(record.key, dataIndex, value);
      setIsEditing(false);
    };

    const handleSelectChange = (selectedValue) => {
      editedRows.current.add(record.key);
      setValue(selectedValue);
      handleCellEdit(record.key, dataIndex, selectedValue);
      setIsEditing(false);
    };

    const inputNode =
      inputType === "number" ? (
        <InputNumber
          value={value === "" || isNaN(value) ? null : value}
          onChange={setValue}
          onPressEnter={handleSave}
          onBlur={handleSave}
          autoFocus
          style={{ width: "100%" }}
          step={dataIndex === "quantity" ? 0.1 : 0.01}
          min={0}
        />
      ) : inputType === "selectUnit" ? (
        <Select
          placeholder="Select unit"
          value={value}
          onChange={handleSelectChange}
          autoFocus
          style={{ width: "100%" }}
          dropdownStyle={{ width: "120px" }}
          showSearch
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
        >
          {unitsOptions?.map((unit) => (
            <Select.Option key={unit.unitID} value={unit.name}>
              {unit.name}
            </Select.Option>
          ))}
        </Select>
      ) : inputType === "wbsCodeID" ? (
        <Select
          placeholder="Select Cost Code"
          value={value}
          onChange={handleSelectChange}
          autoFocus
          style={{ width: "100%" }}
        >
          {wbsOptions?.map((wbs) => (
            <Select.Option key={wbs.wbsID} value={wbs.wbsID.toString()}>
              {wbs.description}
            </Select.Option>
          ))}
        </Select>
      ) : inputType === "materialSelection" ? (
        <Select
          placeholder="Select Material"
          value={value?.toString()}
          onChange={handleSelectChange}
          autoFocus
          style={{ width: "100%" }}
          dropdownStyle={{ width: "300px" }}
          showSearch
          optionFilterProp="children"
        >
          {allMaterials.map((item) => (
            <Select.Option
              key={item.materialID}
              value={item.materialID.toString()}
            >
              {item.materialName} ({item.unit})
            </Select.Option>
          ))}
        </Select>
      ) : (
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onPressEnter={handleSave}
          onBlur={handleSave}
          autoFocus
        />
      );

    const isNewRow = !record?.lineItemID;
    const isMaterialColumn = dataIndex === "itemID";
    const shouldDisableMaterialForNewRow = isNewRow && isMaterialColumn;

    const isEditable =
      dataIndex && !shouldDisableMaterialForNewRow && !readOnlyMode && !isSent;

    const mismatchError = hasUnitMismatch();
    const shouldShowMismatchTooltip =
      isTransferMode && (dataIndex === "itemID" || dataIndex === "unit");

    const cellContent = (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
          cursor: isEditable ? "pointer" : "default",
          minHeight: 32,
          display: "flex",
          alignItems: "center",
          backgroundColor: isEditable && isHovered && "#F0F0F0",
          borderRadius: "4px",
          padding: "4px 8px",
          margin: "-4px -8px",
          transition: "border-color 0.2s ease",
          border:
            hasError() && !readOnlyMode
              ? "1px solid #ff4d4f"
              : "1px solid transparent",
        }}
        onClick={() => isEditable && setIsEditing(true)}
        onMouseEnter={() => isEditable && setIsHovered(true)}
        onMouseLeave={() => isEditable && setIsHovered(false)}
      >
        {children}
        {isSent && dataIndex === "description" && (
          <CheckCircleFilled style={{ marginLeft: 8, opacity: "50%" }} />
        )}
      </div>
    );

    return (
      <td
        {...restProps}
        style={{
          backgroundColor: isSent ? "#fafafa" : "inherit",
        }}
      >
        {isEditing ? (
          inputNode
        ) : shouldShowMismatchTooltip && mismatchError && !readOnlyMode ? (
          <Tooltip
            title="Please convert the PO unit to match the inventory storage unit"
            color="#ff4d4f"
          >
            {cellContent}
          </Tooltip>
        ) : (
          cellContent
        )}
      </td>
    );
  };

  const columns = [
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: "description",
        title: "Description",
      }),
    },
    ...(isTransferMode
      ? [
          {
            title: "Material",
            dataIndex: "itemID",
            key: "itemID",
            render: (value, record) => {
              if (!record.lineItemID) {
                return (
                  <Tooltip title="Save changes to edit field">
                    <div
                      className="w-100"
                      style={{
                        width: "100%",
                        minHeight: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    ></div>
                  </Tooltip>
                );
              }
              const material = getMaterialById(value);
              return material ? `${material.materialName}` : value || "";
            },
            onCell: (record) => ({
              record,
              inputType: "materialSelection",
              dataIndex: "itemID",
              title: "Material",
            }),
          },
          {
            title: "Storage Unit",
            dataIndex: "storageUnit",
            key: "storageUnit",
            render: (_, record) => {
              const material = getMaterialById(record?.itemID);
              return material ? material.unit : "";
            },
            onCell: () => ({}),
          },
        ]
      : []),
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      render: (value) =>
        value !== null && value !== "" && !isNaN(value)
          ? parseFloat(value).toFixed(1)
          : "",
      onCell: (record) => ({
        record,
        inputType: "number",
        dataIndex: "quantity",
        title: "Quantity",
      }),
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      onCell: (record) => ({
        record,
        inputType: "selectUnit",
        dataIndex: "unit",
        title: "Unit",
      }),
    },
    {
      title: "Unit Cost",
      dataIndex: "unitCost",
      key: "unitCost",
      render: (value) =>
        value !== null && value !== "" && !isNaN(value)
          ? parseFloat(value).toFixed(2)
          : "",
      onCell: (record) => ({
        record,
        inputType: "number",
        dataIndex: "unitCost",
        title: "Unit Cost",
      }),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (value) => `${parseFloat(value).toFixed(2)}`,
      onCell: () => ({}),
    },
    {
      title: "Cost Code",
      dataIndex: "wbsCodeID",
      key: "wbsCodeID",
      render: (value) => {
        const wbs = wbsOptions?.find(
          (w) => w.wbsID.toString() === value.toString(),
        );
        return wbs ? `${wbs.description}` : value;
      },
      onCell: (record) => ({
        record,
        inputType: "wbsCodeID",
        dataIndex: "wbsCodeID",
        title: "WBS Code ID",
      }),
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (_, record) => {
        if (!record.lineItemID && !readOnlyMode) {
          return (
            <Popconfirm
              title="Delete this line item?"
              description="Are you sure you want to delete this line item?"
              onConfirm={() => handleDeleteLineItem(record.key)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" danger>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          );
        }
        return null;
      },
      onCell: () => ({}),
    },
  ];

  const components = {
    body: {
      cell: EditableCell,
    },
  };

  const handleSubmit = (values) => {
    let code = AuthStorage.getItem("token");
    let email = AuthStorage.getItem("email");

    const ReqObj = {
      company: company_id,
      code,
      loginEmail: email,
      values,
    };

    WarehouseServices.createWarehouseItem(ReqObj)
      .then(() => {
        toast.success("Material created!");
        if (updateMaterials) {
          updateMaterials();
        }
      })
      .catch(() => {
        toast.error("Something went wrong");
      });

    setIsModalVisible(false);
  };

  const handleCancelModal = () => {
    form.resetFields();
    setIsModalVisible(false);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.transferStatus === "Sent",
      name: record.key,
    }),
    columnWidth: 50,
    renderCell: (_, record, index, node) => {
      return (
        <div
          style={{ display: "flex", justifyContent: "center", width: "100%" }}
        >
          {node}
        </div>
      );
    },
  };

  const isTransferDisabled =
    hasValidationMaterialsErrors ||
    hasValidationErrors ||
    lineItems.length === 0 ||
    newRows.current.size > 0 ||
    (selectedRowKeys.length === 0 &&
      lineItems.some((item) => item.transferStatus !== "Sent"));

  const selectedUnsentCount = selectedRowKeys?.filter((key) => {
    const item = lineItems.find((i) => i.key === key);
    return item && item.transferStatus !== "Sent";
  }).length;

  const isMarkCompletedDisabled =
    loading ||
    saving ||
    transferring ||
    completing ||
    hasChanges ||
    !record?.purchaseOrderID ||
    !(projectID || record?.projectID);

  const markCompletedTooltip = isMarkCompletedDisabled
    ? hasChanges
      ? "Save or discard your changes before marking this purchase order as completed"
      : !record?.purchaseOrderID || !(projectID || record?.projectID)
        ? "Missing purchase order information"
        : "This purchase order can't be marked as completed right now"
    : "";

  return (
    <Modal
      title=""
      open={isPostLineItemVisible}
      centered
      onCancel={handleCancel}
      width={1400}
      footer={
        <div className=" d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex gap-2 order-1">
            <Button key="cancel" onClick={handleCancel}>
              {readOnlyMode ? "Close" : "Cancel"}
            </Button>
            {!readOnlyMode && (
              <Button
                key="save"
                type="primary"
                onClick={handleSave}
                loading={saving}
                disabled={!hasChanges || hasValidationErrors}
              >
                Save Changes
              </Button>
            )}
          </div>

          <div className="order-2 ">
            {!readOnlyMode && accountingMethod === "amount" && (
              <Alert
                style={{ maxWidth: "500px" }}
                message="Unit/Qty changes for this PO will only reflect on Koncurent"
              />
            )}
          </div>

          <div className="d-flex gap-2 order-3 justify-content-end ms-auto">
            {isTransferMode && !readOnlyMode && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Tooltip title={markCompletedTooltip}>
                    <Button
                      danger
                      onClick={handleMarkPurchaseOrderCompleted}
                      disabled={isMarkCompletedDisabled}
                      loading={completing}
                    >
                      Mark as Completed
                    </Button>
                  </Tooltip>
                  <Tooltip
                    title={
                      isTransferDisabled
                        ? newRows.current.size > 0
                          ? "Must save new items before transfer"
                          : lineItems.length === 0
                            ? "No line items to transfer"
                            : hasValidationMaterialsErrors ||
                                hasValidationErrors
                              ? "Please resolve validation errors"
                              : selectedRowKeys.length === 0 &&
                                  lineItems.every(
                                    (item) => item.transferStatus === "Sent",
                                  )
                                ? "All items have been transferred"
                                : ""
                        : ""
                    }
                  >
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button
                        type="primary"
                        onClick={() => handleTransfer(false)}
                        disabled={
                          completing ||
                          isTransferDisabled ||
                          selectedUnsentCount === 0
                        }
                        loading={transferring && !completing}
                      >
                        Transfer {selectedUnsentCount} to Inventory
                      </Button>
                      <Button
                        onClick={() => handleTransfer(true)}
                        disabled={
                          completing ||
                          isTransferDisabled ||
                          selectedUnsentCount === 0
                        }
                        loading={transferring && !completing}
                      >
                        Transfer {selectedUnsentCount} to Inventory and Mark
                        Completed
                      </Button>
                    </div>
                  </Tooltip>
                </div>
            )}
          </div>
        </div>
      }
    >
      <div className=" d-flex flex-column gap-3 mt-5">
        <div className=" w-100 d-flex justify-content-between align-items-center">
          <div className=" d-flex gap-3 align-baseline ">
            <h5 className=" fw-bold">{record?.title}</h5>
            <div>
              {/* Only show the full checkmark if ALL items are sent */}
              {readOnlyMode && (
                <Tooltip title="Fully Transferred to Inventory">
                  <h5>
                    <CheckCircleFilled className=" opacity-50" />
                  </h5>
                </Tooltip>
              )}
              {/* {readOnlyMode &&
                lineItems.every((item) => item.transferStatus === "Sent") && (
                  <Tooltip title="Fully Transferred to Inventory">
                    <h5>
                      <CheckCircleFilled className=" opacity-50" />
                    </h5>
                  </Tooltip>
                )} */}
            </div>
          </div>
          {!readOnlyMode && (
            <div className=" d-flex gap-2">
              <Button
                key="addNew"
                // type="primary"
                onClick={() => {
                  setIsModalVisible(true);
                  form.resetFields();
                }}
                disabled={loading}
                size="large"
              >
                {/* <PlusOutlined /> */}
                Add Material
              </Button>

              <Button
                key="addNew"
                type="primary"
                onClick={handleAddLineItem}
                disabled={loading}
                size="large"
              >
                <PlusOutlined />
                Add Line Item
              </Button>
            </div>
          )}
        </div>

        <MaterialFormModal
          open={isModalVisible}
          isEditing={false}
          // selectedItem={selectedItem}
          categoryOptions={categoryOptions}
          subcategoryOptions={subcategoryOptions}
          unitsOptions={unitsOptions}
          locationsOptions={locationsOptions}
          // loadingPrices={isLoadingPrices}
          onCancel={handleCancelModal}
          onSubmit={handleSubmit}
          // onHistoryClick={handlePriceHistory}
        />

        <Table
          components={components}
          dataSource={lineItems}
          columns={columns}
          pagination={false}
          loading={{
            spinning: loading,
            indicator: <LoadingComponent />,
          }}
          bordered
          rowKey="key"
          scroll={{ x: 800 }}
          // *** MODIFICATION: Conditionally add rowSelection for transfer mode ***
          rowSelection={
            isTransferMode && !readOnlyMode ? rowSelection : undefined
          }
        />
      </div>
    </Modal>
  );
};

export default LineItemsModal;
